import React from 'react';
import styled from 'styled-components';
import toRem from 'utils/toRem';
import handleNestedId from '../lib/handleNestedId.ts';
import Legend from '../partials/Legend/index.tsx';
import InputError from '../partials/InputError/index.tsx';

export interface RadioContainerProps {
  className?: string;
  direction?: 'vertical' | 'horizontal';
  variant?: string;
  legend?: string;
  description?: string;
  groupName: string;
  children: React.ReactElement | Array<React.ReactElement | null>;
  errors?: { [key: string]: { type: string; message: string } };
}

interface Direction {
  direction?: 'vertical' | 'horizontal';
}

const StyledRadioContainer = styled.fieldset`
  margin: 0;
  padding: 0;
  border: 0;
`;

const StyledRadioInner = styled.div<Direction>`
  display: flex;
  gap: ${toRem(8)} ${toRem(20)};
  flex-direction: ${({ direction }) =>
    direction === 'horizontal' ? 'row' : 'column'};
`;

const RadioContainer = ({
  className,
  direction = 'vertical',
  variant,
  legend,
  description,
  groupName,
  children,
  errors,
}: RadioContainerProps) => {
  const fieldId = handleNestedId(groupName);
  const hasErrors = !!errors && !!Object.keys(errors).length;

  // Pass the 'groupName' prop to each radio button, and assign to the 'name' prop for each
  const childrenWithGroupName = React.Children.map(children, child => {
    if (child !== null) {
      return React.cloneElement(child, {
        name: groupName,
        hasErrors,
      });
    }

    return null;
  });

  return (
    <StyledRadioContainer
      className={`cef-ui-radio-container${className ? ` ${className}` : ''}`}>
      {legend && (
        <Legend variant={variant} legend={legend} description={description} />
      )}

      <StyledRadioInner direction={direction} role="radiogroup">
        {childrenWithGroupName}
      </StyledRadioInner>

      {hasErrors && fieldId && errors[fieldId] && (
        <InputError errorMessage={errors[fieldId].message} />
      )}
    </StyledRadioContainer>
  );
};

export default RadioContainer;
